import { Text } from "@/components/podkit/typography/Text";
import { type PlainMessage } from "@bufbuild/protobuf";
import { type WorkflowStep } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { useMemo, type FC } from "react";
import IconConsoleSimple from "central-icons/IconConsoleSimple";
import IconBubbleText6 from "central-icons/IconBubbleText6";
import { StepEllipse } from "@/components/automations/StepEllipse";
import { StepVerticalLine } from "@/components/automations/StepVerticalLine";
import { cn } from "@/components/podkit/lib/cn";

type WorkflowStepCardProps = {
    index: number;
    step: PlainMessage<WorkflowStep>;
};

export const WorkflowStepCard: FC<WorkflowStepCardProps> = ({ step, index }) => {
    const {
        type,
        content,
        icon: Icon,
    } = useMemo(() => {
        switch (step.step?.case) {
            case "task":
                return {
                    type: "Shell script",
                    content: step.step.value.command || "",
                    icon: IconConsoleSimple,
                };
            case "agent":
                return {
                    type: "Prompt",
                    content: step.step.value.prompt || "",
                    icon: IconBubbleText6,
                };
            default:
                return {
                    type: "Unknown",
                    content: "",
                    icon: IconConsoleSimple, // Default icon
                };
        }
    }, [step]);

    const isFirst = index === 0;

    return (
        <div className="flex items-start gap-2.5">
            <div className="flex flex-col items-center self-stretch">
                <StepVerticalLine
                    className={cn("h-[7px] grow-0", {
                        "border-transparent": isFirst,
                    })}
                />
                <StepEllipse className="text-border-strong" />
                <StepVerticalLine />
            </div>
            <div className="flex flex-grow flex-col gap-1 pb-2">
                <div className="flex items-center gap-1 py-1">
                    <Text className="text-sm font-medium text-content-muted">{index+1}:</Text>
                    <Icon size={16} className="text-content-muted" />
                    <Text className="text-sm font-normal text-content-muted">{type}</Text>
                </div>

                <div className="rounded-lg border border-border-subtle bg-surface-primary px-2 py-1">
                    <Text className="whitespace-pre-wrap break-words p-2 font-mono text-sm text-content-primary">
                        {content || <span className="italic text-content-tertiary">No content</span>}
                    </Text>
                </div>
            </div>
        </div>
    );
};
